// AuthContext — Guardian JWT via httpOnly cookies (T201/T203).
// Cookie is set by backend on login/register/OAuth exchange.
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import authApi, { setImpersonationActive } from "../services/authApi";
import { requiresSetup } from "../utils/setupAccess";

const AuthContext = createContext();
const extractProfile = (response) =>
  response?.user || response?.data || response;

export const useAuth = () => useContext(AuthContext);

// Backoff for a session probe that was throttled or hit a server error; the
// total (~65 s) outlasts the API rate limiter's one-minute window.
const SESSION_PROBE_RETRY_MS = [1500, 4000, 10000, 20000, 30000];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [socketCredential, setSocketCredential] = useState(null);
  const [notification, setNotification] = useState(null);
  const setupRequired = requiresSetup(user);
  const socketToken =
    !setupRequired &&
    socketCredential &&
    user?.id &&
    socketCredential.userId === user.id
      ? socketCredential.token
      : null;

  // Inside an entered workspace a lapsed token refreshes as the super admin;
  // authApi reloads into their own workspace when that happens.
  useEffect(() => {
    setImpersonationActive(Boolean(user?.impersonating));
  }, [user?.impersonating]);

  // Auto-clear error after 8s
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Auto-clear notification after 5s
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // On mount — check session only when the backend nd_session marker is present.
  // nd_session=1 is a non-httpOnly cookie set by the backend on login/refresh success
  // and cleared on logout or refresh failure. Guests have no marker → no probe → no 401.
  useEffect(() => {
    const hasSession = document.cookie
      .split(";")
      .some((c) => c.trim().startsWith("nd_session="));

    if (!hasSession) {
      localStorage.removeItem("nexus_errors");
      setLoading(false);
      return;
    }

    // A throttled (429) or failing (5xx) probe is not a signed-out user:
    // bouncing to /login there logged people out mid-demo whenever the API
    // rate limit tripped. Retry across the limiter's 60 s window instead.
    let cancelled = false;
    let retryTimer;

    const probe = (attempt) =>
      authApi
        .me()
        .then((response) => {
          if (cancelled) return;
          setUser(extractProfile(response));
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          const transient = err?.status === 429 || err?.status >= 500;
          if (transient && attempt < SESSION_PROBE_RETRY_MS.length) {
            retryTimer = window.setTimeout(
              () => probe(attempt + 1),
              SESSION_PROBE_RETRY_MS[attempt],
            );
            return;
          }
          setLoading(false);
        });

    probe(0);

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
    };
  }, []);

  // Exchange the HttpOnly browser session for a short-lived, socket-only
  // credential. Keep it in memory and rotate it before Phoenix reconnects.
  useEffect(() => {
    setSocketCredential(null);
    if (!user?.id || setupRequired) return undefined;

    let active = true;
    let refreshTimer;

    const refreshSocketToken = async () => {
      try {
        const ticket = await authApi.socketTicket();
        if (!active) return;

        const token = ticket?.socketToken || ticket?.socket_token;
        const expiresIn = Number(ticket?.expiresIn || ticket?.expires_in);
        if (!token || !Number.isFinite(expiresIn)) {
          throw new Error("Invalid socket ticket response");
        }

        setSocketCredential({ token, userId: user.id });
        refreshTimer = window.setTimeout(
          refreshSocketToken,
          Math.max((expiresIn - 30) * 1000, 10000),
        );
      } catch (_) {
        if (!active) return;
        setSocketCredential(null);
        refreshTimer = window.setTimeout(refreshSocketToken, 5000);
      }
    };

    refreshSocketToken();

    return () => {
      active = false;
      window.clearTimeout(refreshTimer);
    };
  }, [user?.id, setupRequired]);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const data = await authApi.login(email, password);
      const profile = extractProfile(data);
      // No success toast: landing on Home already says the sign-in worked.
      setUser(profile);
      return profile;
    } catch (err) {
      const msg = err.message || "Login failed.";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (userData) => {
    setLoading(true);
    try {
      const data = await authApi.register(userData);
      const profile = extractProfile(data);
      setUser(profile);
      setNotification({
        type: "success",
        message: "Account created",
      });
      return profile;
    } catch (err) {
      const msg = err.message || "Registration failed.";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (_) {
      // always clear local state
    }
    setUser(null);
    setNotification({ type: "info", message: "Signed out" });
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    try {
      const response = await authApi.updateProfile(profileData);
      const updated = extractProfile(response);
      setUser(updated);
      setNotification({
        type: "success",
        message: "Profile saved",
      });
      return updated;
    } catch (err) {
      const msg = err.message || "Failed to update profile.";
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  // OAuth: receives one-time code from OAuthCallback, exchanges for cookie session
  const completeOAuthLogin = useCallback(async ({ code }) => {
    setLoading(true);
    setError(null);

    try {
      await authApi.exchangeOAuthCode(code);
      const response = await authApi.me();
      const profile = extractProfile(response);

      setUser(profile);

      return profile;
    } catch (err) {
      const msg = err.message || "OAuth sign-in failed.";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUserStatus = useCallback((newStatus) => {
    setUser((prev) => (prev ? { ...prev, status: newStatus } : prev));
  }, []);

  const value = {
    user,
    socketToken,
    loading,
    error,
    notification,
    isLoaded: !loading,
    isSignedIn: !!user,
    login,
    register,
    logout,
    updateProfile,
    completeOAuthLogin,
    updateUserStatus,
    setError,
    setNotification,
  };

  return (
    <AuthContext.Provider value={value}>
      {notification && (
        // Same column and look as the toaster (ui/SignalToaster): bottom
        // right above the call dock, a left rule per type.
        <div className="sg-toaster sg-auth-note" role="status" aria-live="polite">
          <div
            className="sg-toast"
            data-tone={notification.type === "error" ? "error" : notification.type === "success" ? "success" : "neutral"}
          >
            <p className="sg-toast-msg">{notification.message}</p>
          </div>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
